/* Playo — live preview (the channel's public master.m3u8 in hls.js, or
   native HLS on Safari) + a "Now / Next" panel. */

import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import um from "../userManagement/userManagement.module.css";
import s from "./Playo.module.css";
import { getPlaybackUrl, getSchedule, testSource, apiErrorMessage } from "./playoService";
import { DAY, formatTime, pickNowNext } from "./playoTime";

const REFRESH_MS = 30 * 1000;
// A signed playback URL is fetched again this long before it expires …
const TOKEN_RENEW_BEFORE_MS = 5 * 60 * 1000;
// … or when the player fails, at most this often.
const TOKEN_RETRY_MS = 60 * 1000;

export function NowNext({ channel, items, now = Date.now() }) {
  const guideOnly = channel.source_type === "external";
  const { now: cur, next } = pickNowNext(items, now);
  const tz = channel.timezone || "UTC";
  const progress = cur ? Math.min(100, Math.max(0, ((now - cur.start) / (cur.end - cur.start)) * 100)) : 0;
  return (
    <div className={s.nowNext} aria-label="Now and next">
      <div className={s.nnBlock}>
        <div className={s.nnLabel}>Now</div>
        {cur ? (
          <>
            <div className={s.nnTitle} data-testid="now-title">
              {cur.title || "Untitled"}
            </div>
            <div className={s.muted}>
              {formatTime(cur.start, tz)} – {formatTime(cur.end, tz)}
            </div>
            <div className={s.progress}>
              <div className={s.progressFill} style={{ width: `${progress}%` }} />
            </div>
          </>
        ) : (
          <div className={s.muted} data-testid="now-title">
            {guideOnly ? "Live stream (no guide entry now)" : "Gap: filler is playing"}
          </div>
        )}
      </div>
      <div className={s.nnBlock}>
        <div className={s.nnLabel}>Next</div>
        {next ? (
          <>
            <div className={s.nnTitle} data-testid="next-title">
              {next.title || "Untitled"}
            </div>
            <div className={s.muted}>{formatTime(next.start, tz)}</div>
          </>
        ) : (
          <div className={s.muted} data-testid="next-title">
            Nothing scheduled in the next 7 days
          </div>
        )}
      </div>
    </div>
  );
}

/** External channel: where the picture comes from, its last health check, "Check now". */
function SourceCheck({ channel }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const status = result ? result.status : channel.source_status;
  const error = result ? result.error : channel.source_error;
  const check = async () => {
    setBusy(true);
    try {
      setResult(await testSource(channel.source_url, channel.id));
    } catch (e) {
      setResult({ ok: false, status: "offline", error: apiErrorMessage(e, "Could not check the stream") });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={s.nnBlock} data-testid="source-check">
      <div className={s.nnLabel}>Source stream</div>
      <div className={s.urlRow}>
        <code className={s.url} title={channel.source_url}>
          {channel.source_url}
        </code>
        <button type="button" className={um.btnSubtle} onClick={check} disabled={busy}>
          {busy ? "Checking…" : "Check now"}
        </button>
      </div>
      <div className={status === "online" ? s.okText : status === "offline" ? s.warnText : s.muted}>
        {status === "online" ? "Online" : status === "offline" ? `Offline${error ? `: ${error}` : ""}` : "Not checked yet"}
      </div>
    </div>
  );
}

function Player({ url, onFatal }) {
  const ref = useRef(null);
  const [error, setError] = useState("");
  const fatal = useRef(onFatal);
  fatal.current = onFatal;
  useEffect(() => {
    const video = ref.current;
    if (!video || !url) return undefined;
    setError("");
    let hls = null;
    if (Hls.isSupported()) {
      hls = new Hls({ liveSyncDurationCount: 3 });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        // The token may have run out: a fresh URL is on its way.
        if (data?.fatal && !fatal.current?.()) {
          setError("The live stream couldn't be loaded (off air, the source is down, or no channel-ready content yet).");
        }
      });
      hls.loadSource(url);
      hls.attachMedia(video);
    } else if (video.canPlayType && video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
    } else {
      setError("This browser can't play HLS.");
    }
    return () => {
      if (hls) hls.destroy();
    };
  }, [url]);
  return (
    <>
      <video ref={ref} className={s.video} controls muted autoPlay playsInline data-testid="preview-video" />
      {error && <div className={s.warnText}>{error}</div>}
    </>
  );
}

export default function PlayoPreview({ channel }) {
  const [items, setItems] = useState([]);
  const [tick, setTick] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const [playback, setPlayback] = useState(null);
  const [playbackError, setPlaybackError] = useState("");
  const [renewal, setRenewal] = useState(0);
  const lastFetch = useRef(0);
  const url = playback?.url || "";

  // The tenant may require login, in which case the public playlists need a
  // signed token: ask the backend for the URL instead of building it here.
  useEffect(() => {
    let alive = true;
    if (!renewal) setPlayback(null); // a renewal keeps playing until the new URL is in
    setPlaybackError("");
    lastFetch.current = Date.now();
    getPlaybackUrl(channel.id)
      .then((p) => alive && setPlayback(p))
      .catch((e) => alive && setPlaybackError(apiErrorMessage(e, "Could not get the stream URL")));
    return () => {
      alive = false;
    };
  }, [channel.id, channel.slug, channel.status, renewal]);

  // A signed URL expires (PLAYBACK_TOKEN_TTL_SEC): fetch a new one before it does.
  const expiresAt = playback?.login_required && playback.expires_at ? new Date(playback.expires_at).getTime() : null;
  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setTimeout(() => setRenewal((n) => n + 1), Math.max(TOKEN_RETRY_MS, expiresAt - TOKEN_RENEW_BEFORE_MS - Date.now()));
    return () => clearTimeout(id);
  }, [expiresAt]);

  // The player failed: with a signed URL that may be an expired token (e.g. the
  // laptop slept through the renewal), so fetch a new one — once a minute at most.
  const renewOnFatal = () => {
    if (!playback?.login_required || Date.now() - lastFetch.current < TOKEN_RETRY_MS) return false;
    setRenewal((n) => n + 1);
    return true;
  };

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const t = Date.now();
      try {
        const list = await getSchedule(channel.id, t - DAY, t + 7 * DAY);
        if (alive) setItems(list);
      } catch {
        /* keep the last answer */
      }
      if (alive) setTick(Date.now());
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [channel.id]);

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <section className={s.previewCard} aria-label="Preview">
      <div className={s.sectionHead}>Preview</div>
      {channel.status === "active" ? (
        <Player url={url} onFatal={renewOnFatal} />
      ) : (
        <div className={s.previewOff}>Draft channels aren't on air. Set the status to Active to preview the live stream.</div>
      )}
      <div className={s.urlRow}>
        <code className={s.url} title={url}>
          {url}
        </code>
        <button type="button" className={um.btnSubtle} onClick={copy} disabled={!url}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {playbackError && <div className={s.warnText}>{playbackError}</div>}
      {playback?.login_required && (
        <div className={s.muted} data-testid="token-note">
          This tenant requires sign-in, so this URL carries a playback token that expires{" "}
          {playback.expires_at ? new Date(playback.expires_at).toLocaleString() : "soon"}. Don't hand it to partners as a permanent feed.
        </div>
      )}
      {channel.source_type === "external" && <SourceCheck channel={channel} />}
      <NowNext channel={channel} items={items} now={tick} />
    </section>
  );
}
